import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Tabulation from 'components/Tabulation';
import SearchFilter from 'components/SearchFilter';
import { Button, Icon } from '@material-ui/core';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { actions as modalActions } from 'redux/modal';
import { SendFileForm } from 'components/CommonForms';
import { PageSelect } from 'components/Selects';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import DocumentsTable from '../CourseDetails/DocumentsTable';
import RequiredSessionsTable from './RequiredSessionsTable';
import SessionsInProgressTable from './SessionsInProgressTable';
import SessionsScheduledTable from './SessionsScheduledTable';
import SessionsPastTable from './SessionsPastTable';
import EvidenceHistoryTable from './EvidenceHistoryTable';
import ConfirmExternalEvidenceForm from './ConfirmExternalEvidenceForm';

const DEFAULT_PAGE_SIZE = 5;

const getTabActionComponent = (title, icon, action = () => {}, disabled = false) => (
  <Button onClick={action} disabled={disabled} variant="contained" color="primary" startIcon={<Icon>{icon}</Icon>}>
    {title}
  </Button>
);

const useStyles = makeStyles(() => ({
  searchField: {
    '& > div': {
      marginRight: 0,
    },
  },
}));

const TrainingSessionDetails = ({ userId, helpers, routesAliases, isUserDataLoaded, assignedJobTasks }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const [requiredSessionsSaveState, setRequiredSessionsSaveState] = useState({
    hasChanges: false,
    isLoading: false,
    onSave: () => {},
  });

  const [activeTab, setActiveTab] = useState({
    tabIndex: 0,
    alias: 'requiredSessions',
  });
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState({ field: 'id', dir: 'desc' });
  const [filtering, setFiltering] = useState({});
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const forceUpdateRef = useRef(0);
  const [forceUpdateTick, setForceUpdateTick] = useState(0);

  const historyState = {};

  const aliasFetchMap = useMemo(
    () => ({
      requiredSessions: helpers.getRequiredSessions,
      inProgress: helpers.getSessionsInProgress,
      scheduled: helpers.getSessionsScheduled,
      past: helpers.getSessionsPassed,
      history: helpers.getEvidencesHistory,
      documents: helpers.getDocuments,
    }),
    [helpers],
  );

  const handleForceUpdate = useCallback(() => {
    forceUpdateRef.current += 1;
    setForceUpdateTick(forceUpdateRef.current);
  }, []);

  const [fetchState, fetchTabData] = useAsyncFn(
    async (alias, nextPage, nextSorting, nextFiltering) => {
      const method = aliasFetchMap[alias];
      if (!method) {
        setRows([]);
        setTotalCount(0);
        return null;
      }

      const sortingParams =
        nextSorting?.field && nextSorting?.dir ? { [nextSorting.field]: nextSorting.dir } : { id: 'desc' };

      const response = await method({
        page: nextPage,
        pageSize: DEFAULT_PAGE_SIZE,
        ...sortingParams,
        ...nextFiltering,
      });

      if (!response?.error && !(response?.statusCode && response.statusCode >= 400)) {
        const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [];
        setRows(items);
        setTotalCount(response?.count ?? items.length);
      }
      return response;
    },
    [aliasFetchMap],
  );

  const handleTabChange = (e, data) => {
    setPage(0);
    setSorting({ field: 'id', dir: 'desc' });
    setFiltering({});
    setActiveTab({
      tabIndex: data.tabIndex,
      alias: data.alias,
    });
  };

  if (history.action === 'PUSH' && location.state) {
    historyState.tabIndex = location.state.trainingSessionTabIndex || 0;
    historyState.alias = location.state.trainingSessionAlias || 'requiredSessions';
  }

  const handleRequiredSessionsSaveStateChange = useCallback(nextState => {
    setRequiredSessionsSaveState(prevState => {
      if (
        prevState.hasChanges === nextState.hasChanges &&
        prevState.isLoading === nextState.isLoading &&
        prevState.onSave === nextState.onSave
      ) {
        return prevState;
      }
      return nextState;
    });
  }, []);

  const tableData = useMemo(
    () => ({
      data: rows,
      totalCount,
      pageSize: DEFAULT_PAGE_SIZE,
      isFetchingTableData: fetchState.loading,
      handleChangePage: nextPage => setPage(nextPage),
      handleSorting: (field, dir) => setSorting({ field, dir }),
      handleFiltering: (key, value) => {
        setPage(0);
        setFiltering(prev => ({ ...prev, [key]: value }));
      },
      handleRemoveFilter: key => {
        setPage(0);
        setFiltering(prev => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      },
      handleForceUpdate,
    }),
    [rows, totalCount, fetchState.loading, handleForceUpdate],
  );

  const RequiredSessionsComponent = (
    <RequiredSessionsTable
      tableData={tableData}
      userId={userId}
      helpers={{
        applyRequiredSessions: helpers.applyRequiredSessions,
      }}
      routesAliases={routesAliases}
      onSaveStateChange={handleRequiredSessionsSaveStateChange}
    />
  );

  const SessionsInProgressComponent = (
    <SessionsInProgressTable
      tableData={tableData}
      userId={userId}
      helpers={{
        editSession: helpers.editSession,
        removeParticipant: helpers.removeParticipant,
      }}
      routesAliases={routesAliases}
    />
  );

  const SessionsScheduledComponent = (
    <SessionsScheduledTable
      tableData={tableData}
      helpers={{
        editSession: helpers.editSession,
        removeParticipant: helpers.removeParticipant,
      }}
    />
  );

  const SessionsPastComponent = (
    <SessionsPastTable
      tableData={tableData}
      userId={userId}
      helpers={{
        setCertificate: helpers.setCertificate,
        downloadCertificate: helpers.downloadCertificate,
        deleteCertificate: helpers.deleteCertificate,
      }}
    />
  );

  const EvidenceHistoryComponent = (
    <EvidenceHistoryTable
      tableData={tableData}
      userId={userId}
      helpers={{
        updateExternalEvidence: helpers.updateExternalEvidence,
        deleteExternalEvidence: helpers.deleteExternalEvidence,
        uploadEvidenceCertificate: helpers.uploadEvidenceCertificate,
        downloadEvidenceCertificate: helpers.downloadEvidenceCertificate,
        deleteEvidenceCertificate: helpers.deleteEvidenceCertificate,
      }}
    />
  );

  const DocumentsTableComponent = (
    <DocumentsTable
      tableData={tableData}
      userId={userId}
      helpers={{
        getDocuments: helpers.getDocuments,
        setDocuments: helpers.setDocuments,
        downloadDocument: helpers.downloadDocument,
        deleteDocument: helpers.deleteDocument,
        getEditorData: helpers.getEditorData,
      }}
    />
  );

  const [addFileState, addFile] = useAsyncFn(async formData => {
    const data = await helpers.setDocuments(formData);
    if (!data.error) {
      dispatch(modalActions.closeModal());
      handleForceUpdate();
    }
  });

  const [createEvidenceState, createExternalEvidence] = useAsyncFn(async payload => {
    const response = await helpers.createExternalEvidence(payload);
    if (!response?.error && !(response?.statusCode && response.statusCode >= 400)) {
      dispatch(modalActions.closeModal());
      handleForceUpdate();
    }
    return response;
  });

  const handleSubmitFileForm = values => {
    addFile(values);
  };

  const handleAddDocument = () => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingDetails.addDocumentModalTitle'),
        },
        <SendFileForm
          onSubmitForm={handleSubmitFileForm}
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={addFileState.loading}
          fileFormat=".pdf, .pptx, .doc, .docx, .xlsx, .ppt, .jpeg, .jpg, .png"
        />,
      ),
    );
  };

  const handleConfirmExternalEvidence = () => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessionDetails.confirmExternalEvidenceTitle'),
        },
        <ConfirmExternalEvidenceForm
          onSubmitForm={createExternalEvidence}
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={createEvidenceState.loading}
        />,
      ),
    );
  };

  const [importState, importEvidenceFile] = useAsyncFn(async formData => {
    const importResponse = await helpers.importEvidences(formData);
    if (!importResponse?.error && !(importResponse?.statusCode && importResponse.statusCode >= 400)) {
      dispatch(modalActions.closeModal());
      handleForceUpdate();
    }
    return importResponse;
  });

  const [, downloadEvidencesExample] = useAsyncFn(async () => {
    await helpers.downloadEvidencesExample();
  });

  const handleImportEvidences = () => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingSessionDetails.importEvidenceModalTitle'),
        },
        <SendFileForm
          onSubmitForm={values => importEvidenceFile({ data: values.file })}
          formType="import"
          rejectAction={() => {
            dispatch(modalActions.closeModal());
          }}
          submitting={importState.loading}
          fileFormat=".xlsx, .csv"
        />,
      ),
    );
  };

  const createJobsTasksList = useCallback(
    items => {
      const jobsTasksList = [
        {
          label: t('courses.allJobTasks'),
          value: '',
        },
      ];

      const normalizedItems = Array.isArray(items)
        ? items
            .map(item => ({
              id: item?.id ?? item?.value,
              name: item?.name ?? item?.label,
            }))
            .filter(item => item.id !== undefined && !!item.name)
        : [];

      const uniqueItems = normalizedItems.filter(
        (item, index, array) => array.findIndex(current => current.id === item.id) === index,
      );

      uniqueItems.forEach(item =>
        jobsTasksList.push({
          label: item.name,
          value: item.id,
        }),
      );

      return jobsTasksList;
    },
    [t],
  );

  const [jobsTasksFilterValues, setJobsTasksFilterValues] = useState(() => createJobsTasksList(assignedJobTasks));
  const [jobsTasksFilterValue, setJobsTasksFilterValue] = useState('');

  useEffect(() => {
    const studentJobsTasks = createJobsTasksList(assignedJobTasks);
    setJobsTasksFilterValues(studentJobsTasks);
    const selectedJobRoleStillExists = studentJobsTasks.some(option => option.value === jobsTasksFilterValue);

    if (!selectedJobRoleStillExists && jobsTasksFilterValue !== '') {
      setJobsTasksFilterValue('');
      setFiltering(prev => {
        const next = { ...prev };
        delete next['job-task-id'];
        return next;
      });
    }
  }, [assignedJobTasks, createJobsTasksList, jobsTasksFilterValue]);

  const handleChangeJobsTasksFilter = value => {
    setJobsTasksFilterValue(value);
    setPage(0);
    setFiltering(prev => {
      const next = { ...prev };
      if (value === '') {
        delete next['job-task-id'];
      } else {
        next['job-task-id'] = value;
      }
      return next;
    });
  };

  useEffect(() => {
    if (Object.keys(historyState).length) {
      handleTabChange(null, {
        tabIndex: historyState.tabIndex,
        alias: historyState.alias,
      });
      delete historyState.tabIndex;
      delete historyState.alias;
      location.state = undefined;
    }
  });

  useEffect(() => {
    if (!isUserDataLoaded) {
      return;
    }
    fetchTabData(activeTab.alias, page, sorting, filtering);
  }, [activeTab, isUserDataLoaded, page, sorting, filtering, forceUpdateTick, fetchTabData]);

  return (
    <Tabulation
      activeTab={activeTab.tabIndex}
      onTabChange={handleTabChange}
      tabsData={[
        {
          label: t('students.requiredSessionsSection'),
          alias: 'requiredSessions',
          Component: RequiredSessionsComponent,
        },
        {
          label: t('trainingSessionDetails.sessionsInProgress'),
          alias: 'inProgress',
          Component: SessionsInProgressComponent,
        },
        {
          label: t('trainingSessionDetails.sessionsScheduled'),
          alias: 'scheduled',
          Component: SessionsScheduledComponent,
        },
        {
          label: t('trainingSessionDetails.pastSessions'),
          alias: 'past',
          Component: SessionsPastComponent,
        },
        {
          label: t('trainingDetails.history'),
          alias: 'history',
          Component: EvidenceHistoryComponent,
        },
        {
          label: t('trainingDetails.documents'),
          alias: 'documents',
          Component: DocumentsTableComponent,
        },
      ]}
      actions={[
        {
          hiddenOnTabs: [1, 2, 3, 4, 5],
          Component: getTabActionComponent(
            t('students.assignSessions'),
            'save',
            () => requiredSessionsSaveState.onSave(),
            !requiredSessionsSaveState.hasChanges || requiredSessionsSaveState.isLoading,
          ),
        },
        {
          hiddenOnTabs: [5],
          Component: (
            <PageSelect
              classes={{ root: classes.pageSelectRoot }}
              options={jobsTasksFilterValues}
              selectedOption={jobsTasksFilterValue}
              onChangeValue={handleChangeJobsTasksFilter}
            />
          ),
        },
        {
          hiddenOnTabs: [0, 1, 2, 3, 5],
          Component: getTabActionComponent(
            t('trainingSessionDetails.confirmExternalEvidence'),
            'add',
            handleConfirmExternalEvidence,
          ),
        },
        {
          hiddenOnTabs: [0, 1, 2, 3, 5],
          Component: getTabActionComponent(t('commonActions.actionImport'), 'import_export', handleImportEvidences),
        },
        {
          hiddenOnTabs: [0, 1, 2, 3, 5],
          Component: getTabActionComponent(
            t('commonActions.actionDownloadExample'),
            'cloud_download',
            downloadEvidencesExample,
          ),
        },
        {
          hiddenOnTabs: [0, 1, 2, 3, 4],
          Component: getTabActionComponent(t('trainingDetails.actionAddDocument'), 'add', handleAddDocument),
        },
        {
          Component: (
            <div className={classes.searchField}>
              <SearchFilter
                styles={{
                  bgColor: '#e5e5e5',
                  searchWidth: '250px',
                }}
                searchAlias="search"
                onSearch={(value, searchAlias) => {
                  setPage(0);
                  setFiltering(prev => ({ ...prev, [searchAlias]: value }));
                }}
                onClearSearch={searchAlias => {
                  setPage(0);
                  setFiltering(prev => {
                    const next = { ...prev };
                    delete next[searchAlias];
                    return next;
                  });
                }}
              />
            </div>
          ),
        },
      ]}
    />
  );
};

TrainingSessionDetails.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isUserDataLoaded: PropTypes.bool,
  assignedJobTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  helpers: PropTypes.shape({
    getDocuments: PropTypes.func,
    setDocuments: PropTypes.func,
    deleteDocument: PropTypes.func,
    downloadDocument: PropTypes.func,
    getRequiredSessions: PropTypes.func,
    applyRequiredSessions: PropTypes.func,
    getSessionsInProgress: PropTypes.func,
    getSessionsScheduled: PropTypes.func,
    getSessionsPassed: PropTypes.func,
    getEvidencesHistory: PropTypes.func,
    createExternalEvidence: PropTypes.func,
    updateExternalEvidence: PropTypes.func,
    deleteExternalEvidence: PropTypes.func,
    uploadEvidenceCertificate: PropTypes.func,
    downloadEvidenceCertificate: PropTypes.func,
    deleteEvidenceCertificate: PropTypes.func,
    importEvidences: PropTypes.func,
    downloadEvidencesExample: PropTypes.func,
    removeParticipant: PropTypes.func,
    editSession: PropTypes.func,
    setCertificate: PropTypes.func,
    getEditorData: PropTypes.func,
    downloadCertificate: PropTypes.func,
    deleteCertificate: PropTypes.func,
  }).isRequired,
  routesAliases: PropTypes.shape({}).isRequired,
};

TrainingSessionDetails.defaultProps = {
  isUserDataLoaded: false,
  assignedJobTasks: [],
};

export default TrainingSessionDetails;
